import clsx from 'clsx';
import { forwardRef, type HTMLAttributes } from 'react';

import {
  type DataTestId,
  type StylingProps,
  type WithChildren,
} from '@dynatrace/strato-components/core';

import {
  pageMainCSS,
  pageMainLeftCornersCSS,
  pageMainRightCornersCSS,
} from './Page.sty.js';
import { PANEL_ORDER, MIN_WIDTHS } from './Page.js';
import { usePanelsContext } from './Panel/hooks/_usePanelsContext.js';
import { Panel } from './Panel/Panel.js';

/**
 * @public
 */
export interface MainProps
  extends WithChildren,
    Omit<HTMLAttributes<HTMLDivElement>, 'onResize' | keyof StylingProps>,
    StylingProps,
    DataTestId {
  /**
   * The minimum width of the main panel in pixels.
   * @defaultValue 320
   */
  minWidth?: number;
}

/**
 * The `Main` component is used inside the `Page` component for displaying content in the corresponding panel.
 * It is the only compound that is mandatory for rendering the `Page`.
 * @public
 */
export const Main = forwardRef<HTMLDivElement, MainProps>(
  (props, forwardedRef) => {
    const {
      children,
      minWidth = MIN_WIDTHS.main,
      className: consumerClassName,
      style: consumerStyle,
      'data-testid': dataTestId,
      ...remainingProps
    }: MainProps = props;

    const {
      state: { panels },
    } = usePanelsContext();

    const hasSidebar = !!panels['sidebar'];
    const hasDetailView = !!panels['details'];

    return (
      <Panel
        {...remainingProps}
        data-testid={dataTestId}
        ref={forwardedRef}
        name="main"
        order={PANEL_ORDER.main}
        minWidth={minWidth}
        dismissible={false}
        className={clsx(consumerClassName, pageMainCSS, {
          [pageMainLeftCornersCSS]: hasSidebar,
          [pageMainRightCornersCSS]: hasDetailView,
        })}
        style={consumerStyle}
      >
        {children}
      </Panel>
    );
  },
);

(Main as typeof Main & { displayName: string }).displayName = 'Page.Main';
